import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './context/AuthContext';
import { TenantProvider, useTenant } from './context/TenantContext';
import { DemoProvider } from './context/DemoContext';
import DemoBar from './components/DemoBar';
import HomePage from './pages/HomePage';
import LibraryPage from './pages/LibraryPage';
import MovePage from './pages/MovePage';
import PatternPage from './pages/PatternPage';
import PatternLibraryPage from './pages/PatternLibraryPage';
import ProfilePage from './pages/ProfilePage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import Navigation from './components/Navigation';
import ProtectedRoute from './components/ProtectedRoute';
import AdminLayout from './admin/components/AdminLayout';
import AdminDashboard from './admin/pages/AdminDashboard';
import StylesManagement from './admin/pages/StylesManagement';
import SkillLevelsManagement from './admin/pages/SkillLevelsManagement';
import InstructorsManagement from './admin/pages/InstructorsManagement';
import MovesManagement from './admin/pages/MovesManagement';
import PatternsManagement from './admin/pages/PatternsManagement';
import MoveTypesManagement from './admin/pages/MoveTypesManagement';
import HandHoldsManagement from './admin/pages/HandHoldsManagement';
import PositionsManagement from './admin/pages/PositionsManagement';
import BreakdownsManagement from './admin/pages/BreakdownsManagement';
import DanceRolesManagement from './admin/pages/DanceRolesManagement';
import BreakdownAnglesManagement from './admin/pages/BreakdownAnglesManagement';
import BreakdownClipTypesManagement from './admin/pages/BreakdownClipTypesManagement';
import DancersManagement from './admin/pages/DancersManagement';
import DemonstrationsManagement from './admin/pages/DemonstrationsManagement';
import MembersManagement from './admin/pages/MembersManagement';
import InvitesManagement from './admin/pages/InvitesManagement';
import BillingManagement from './admin/pages/BillingManagement';
import RedeemInvitePage from './pages/RedeemInvitePage';
import CoachPage from './pages/CoachPage';
import LandingPage from './pages/LandingPage';
import JoinPage from './pages/JoinPage';
import JoinSuccessPage from './pages/JoinSuccessPage';
import { DEMO_STUDIO_URL, isLandingHost } from './config/apiBase';
import './App.css';
import './admin/admin.css';

// Live streaming pages are code-split: hls.js is heavy and only needed here.
const LiveStreamsPage = React.lazy(() => import('./pages/LiveStreamsPage'));
const LiveStreamSchedulePage = React.lazy(() => import('./pages/LiveStreamSchedulePage'));
const LiveStreamWatchPage = React.lazy(() => import('./pages/LiveStreamWatchPage'));
const LiveStreamBroadcastPage = React.lazy(() => import('./pages/LiveStreamBroadcastPage'));

const queryClient = new QueryClient();

// Tenant theme (Platform Hardening: per-tenant branding). Until tenant config is
// API-driven, this build-time switch picks the member-facing theme. Salsa Mundial
// uses light (its logo has dark text); set REACT_APP_TENANT_THEME=dark to opt a
// tenant into the dark theme defined in styles/tokens.css.
const tenantThemeClass = process.env.REACT_APP_TENANT_THEME === 'dark' ? 'theme-dark' : '';

// Member-facing shell: sticky nav + tenant theme. Admin routes stay light.
// API-driven branding (N-65 phase 1): a tenant's primaryColor/accentColor
// override the brand tokens for everything inside the shell.
const PublicShell: React.FC<{ children: React.ReactNode; plain?: boolean }> = ({ children, plain }) => {
  const tenant = useTenant();
  const brandStyle = tenant?.primaryColor
    ? ({
        '--color-brand': tenant.primaryColor,
        ...(tenant.accentColor ? { '--color-gold': tenant.accentColor } : {}),
      } as React.CSSProperties)
    : undefined;
  return (
    <div className={`public-shell ${tenantThemeClass}`.trim()} style={brandStyle}>
      <DemoBar />
      <Navigation />
      {plain ? children : <main className="main-content">{children}</main>}
    </div>
  );
};

// The bare platform domain is the marketing site: '/' is the landing page and
// any other path (old links from when the demo lived here) goes to the demo.
const LandingRoot: React.FC = () => {
  const { pathname, search, hash } = window.location;
  if (pathname !== '/') {
    window.location.replace(`${DEMO_STUDIO_URL}${pathname}${search}${hash}`);
    return null;
  }
  return <LandingPage />;
};

function App() {
  if (isLandingHost(window.location.hostname)) return <LandingRoot />;

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TenantProvider>
        <Router>
          <DemoProvider>
          <div className="App">
            <Routes>
              {/* Public routes */}
              <Route path="/" element={
                <PublicShell>
                  <HomePage />
                </PublicShell>
              } />
              <Route path="/library" element={
                <PublicShell>
                  <PatternLibraryPage />
                </PublicShell>
              } />
              <Route path="/library/moves" element={
                <PublicShell>
                  <LibraryPage />
                </PublicShell>
              } />
              <Route path="/library/moves/:id" element={
                <PublicShell>
                  <MovePage />
                </PublicShell>
              } />
              <Route path="/library/patterns" element={<Navigate to="/library" replace />} />
              <Route path="/library/patterns/:idOrSlug" element={
                <PublicShell>
                  <PatternPage />
                </PublicShell>
              } />
              <Route path="/login" element={
                <PublicShell plain>
                  <LoginPage />
                </PublicShell>
              } />
              <Route path="/signup" element={
                <PublicShell plain>
                  <SignupPage />
                </PublicShell>
              } />
              <Route path="/forgot-password" element={
                <PublicShell plain>
                  <ForgotPasswordPage />
                </PublicShell>
              } />
              <Route path="/join" element={
                <PublicShell>
                  <JoinPage />
                </PublicShell>
              } />
              <Route path="/join/success" element={
                <ProtectedRoute>
                  <PublicShell>
                    <JoinSuccessPage />
                  </PublicShell>
                </ProtectedRoute>
              } />
              <Route path="/profile" element={
                <ProtectedRoute>
                  <PublicShell>
                    <ProfilePage />
                  </PublicShell>
                </ProtectedRoute>
              } />

              {/* Live streaming (auth required, lazy-loaded) */}
              <Route path="/live" element={
                <ProtectedRoute>
                  <PublicShell>
                    <React.Suspense fallback={null}>
                      <LiveStreamsPage />
                    </React.Suspense>
                  </PublicShell>
                </ProtectedRoute>
              } />
              <Route path="/live/schedule" element={
                <ProtectedRoute>
                  <PublicShell>
                    <React.Suspense fallback={null}>
                      <LiveStreamSchedulePage />
                    </React.Suspense>
                  </PublicShell>
                </ProtectedRoute>
              } />
              <Route path="/live/:id" element={
                <ProtectedRoute>
                  <PublicShell>
                    <React.Suspense fallback={null}>
                      <LiveStreamWatchPage />
                    </React.Suspense>
                  </PublicShell>
                </ProtectedRoute>
              } />
              <Route path="/live/:id/broadcast" element={
                <ProtectedRoute>
                  <PublicShell>
                    <React.Suspense fallback={null}>
                      <LiveStreamBroadcastPage />
                    </React.Suspense>
                  </PublicShell>
                </ProtectedRoute>
              } />

              {/* Admin routes */}
              <Route path="/admin" element={
                <ProtectedRoute requireAdmin>
                  <AdminLayout>
                    <AdminDashboard />
                  </AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/styles" element={
                <ProtectedRoute requireAdmin>
                  <AdminLayout>
                    <StylesManagement />
                  </AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/skill-levels" element={
                <ProtectedRoute requireAdmin>
                  <AdminLayout>
                    <SkillLevelsManagement />
                  </AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/instructors" element={
                <ProtectedRoute requireAdmin>
                  <AdminLayout>
                    <InstructorsManagement />
                  </AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/moves" element={
                <ProtectedRoute requireAdmin>
                  <AdminLayout>
                    <MovesManagement />
                  </AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/patterns" element={
                <ProtectedRoute requireAdmin>
                  <AdminLayout>
                    <PatternsManagement />
                  </AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/move-types" element={
                <ProtectedRoute requireAdmin>
                  <AdminLayout>
                    <MoveTypesManagement />
                  </AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/hand-holds" element={
                <ProtectedRoute requireAdmin>
                  <AdminLayout>
                    <HandHoldsManagement />
                  </AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/positions" element={
                <ProtectedRoute requireAdmin>
                  <AdminLayout>
                    <PositionsManagement />
                  </AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/breakdowns" element={
                <ProtectedRoute requireAdmin>
                  <AdminLayout>
                    <BreakdownsManagement />
                  </AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/dance-roles" element={
                <ProtectedRoute requireAdmin>
                  <AdminLayout>
                    <DanceRolesManagement />
                  </AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/breakdown-angles" element={
                <ProtectedRoute requireAdmin>
                  <AdminLayout>
                    <BreakdownAnglesManagement />
                  </AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/breakdown-clip-types" element={
                <ProtectedRoute requireAdmin>
                  <AdminLayout>
                    <BreakdownClipTypesManagement />
                  </AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/dancers" element={
                <ProtectedRoute requireAdmin>
                  <AdminLayout>
                    <DancersManagement />
                  </AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/demonstrations" element={
                <ProtectedRoute requireAdmin>
                  <AdminLayout>
                    <DemonstrationsManagement />
                  </AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/members" element={
                <ProtectedRoute requireAdmin>
                  <AdminLayout>
                    <MembersManagement />
                  </AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/invites" element={
                <ProtectedRoute requireAdmin>
                  <AdminLayout>
                    <InvitesManagement />
                  </AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/billing" element={
                <ProtectedRoute requireAdmin>
                  <AdminLayout>
                    <BillingManagement />
                  </AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/coach" element={
                <ProtectedRoute>
                  <PublicShell>
                    <CoachPage />
                  </PublicShell>
                </ProtectedRoute>
              } />
              <Route path="/redeem-invite" element={
                <PublicShell>
                  <RedeemInvitePage />
                </PublicShell>
              } />
            </Routes>
          </div>
          </DemoProvider>
        </Router>
        </TenantProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
